import type { GlcFcs30dLegendEntry } from './glc-fcs30d.types';

/**
 * Dataset-level constants for GLC_FCS30D (Liu et al. 2024) — single source of
 * truth for the temporal set and the static class legend, shared by the tool
 * store, the year picker, the breakdown donut and the yearly-distribution chart.
 * Contract: ~/.claude/contract/260623_glc_fcs30d.md.
 */

/** The last annual epoch — the default vintage. */
export const GLC_FCS30D_DEFAULT_YEAR = 2022;

/**
 * The first ANNUAL epoch. Earlier epochs (1985/1990/1995) are 5-year snapshots
 * from the `five-years-map` asset, not the annual series — the picker / chart
 * render them distinctly.
 */
export const GLC_FCS30D_ANNUAL_MIN_YEAR = 2000;

/**
 * The 26 DISCRETE epochs, newest-first so the default sits at the top of the
 * picker: annual 2022→2000, then the three 5-yearly snapshots 1995/1990/1985.
 * NOT a contiguous range — 1996–1999 do not exist. Hardcoded per the contract
 * (ask #3: the FE hardcodes the set; there is no discovery endpoint).
 */
export const GLC_FCS30D_YEARS: number[] = [
  ...Array.from({ length: GLC_FCS30D_DEFAULT_YEAR - GLC_FCS30D_ANNUAL_MIN_YEAR + 1 }, (_, i) => GLC_FCS30D_DEFAULT_YEAR - i),
  1995,
  1990,
  1985,
];

/** True for the pre-2000 5-yearly snapshots (rendered distinctly in the picker/chart). */
export const isGlcFcs30dSnapshotYear = (year: number): boolean => year < GLC_FCS30D_ANNUAL_MIN_YEAR;

/**
 * Static 35-class UN-LCCS legend (value → name + hex) shipped by the FE — the BE
 * uses the identical table (contract §Static legend). Class values are the native
 * LCCS codes (10..220), NON-contiguous; join stats/timeseries by `value`, never by
 * array index. `0 = Filled_value` is masked (transparent) — not a legend entry.
 * Several fine classes share a colour (e.g. 10/11, 150/153) — expected; the name
 * disambiguates.
 */
export const GLC_FCS30D_LEGEND: GlcFcs30dLegendEntry[] = [
  { value: 10, name: 'Rainfed cropland', color: '#ffff64' },
  { value: 11, name: 'Herbaceous cover cropland', color: '#ffff64' },
  { value: 12, name: 'Tree/shrub cover cropland', color: '#ffff00' },
  { value: 20, name: 'Irrigated cropland', color: '#aaf0f0' },
  { value: 51, name: 'Open evergreen broadleaved forest', color: '#4c7300' },
  { value: 52, name: 'Closed evergreen broadleaved forest', color: '#006400' },
  { value: 61, name: 'Open deciduous broadleaved forest', color: '#a8c800' },
  { value: 62, name: 'Closed deciduous broadleaved forest', color: '#00a000' },
  { value: 71, name: 'Open evergreen needle-leaved forest', color: '#005000' },
  { value: 72, name: 'Closed evergreen needle-leaved forest', color: '#003c00' },
  { value: 81, name: 'Open deciduous needle-leaved forest', color: '#286400' },
  { value: 82, name: 'Closed deciduous needle-leaved forest', color: '#285000' },
  { value: 91, name: 'Open mixed-leaf forest', color: '#a0b432' },
  { value: 92, name: 'Closed mixed-leaf forest', color: '#788200' },
  { value: 120, name: 'Shrubland', color: '#966400' },
  { value: 121, name: 'Evergreen shrubland', color: '#964b00' },
  { value: 122, name: 'Deciduous shrubland', color: '#966400' },
  { value: 130, name: 'Grassland', color: '#ffb432' },
  { value: 140, name: 'Lichens and mosses', color: '#ffdcd2' },
  { value: 150, name: 'Sparse vegetation', color: '#ffebaf' },
  { value: 152, name: 'Sparse shrubland', color: '#ffd278' },
  { value: 153, name: 'Sparse herbaceous', color: '#ffebaf' },
  { value: 181, name: 'Swamp', color: '#00a884' },
  { value: 182, name: 'Marsh', color: '#73ffdf' },
  { value: 183, name: 'Flooded flat', color: '#9ebb3b' },
  { value: 184, name: 'Saline', color: '#828282' },
  { value: 185, name: 'Mangrove', color: '#f57ab6' },
  { value: 186, name: 'Salt marsh', color: '#66cdab' },
  { value: 187, name: 'Tidal flat', color: '#444f89' },
  { value: 190, name: 'Impervious surfaces', color: '#c31400' },
  { value: 200, name: 'Bare areas', color: '#fff5d7' },
  { value: 201, name: 'Consolidated bare areas', color: '#dcdcdc' },
  { value: 202, name: 'Unconsolidated bare areas', color: '#fff5d7' },
  { value: 210, name: 'Water body', color: '#0046c8' },
  { value: 220, name: 'Permanent ice and snow', color: '#ffffff' },
];

/**
 * Canonical public source for the dataset — the Zenodo record for GLC_FCS30D
 * (Liu et al. 2024). Linked from the breakdown methodology panel and the ported
 * sidebar card.
 */
export const GLC_FCS30D_SOURCE_URL = 'https://zenodo.org/records/8239305';

/**
 * Snapshot caveat (contract ask #4). GLC_FCS30D is a per-year snapshot series
 * with identical class codes across epochs, but each epoch is classified largely
 * independently — raw year-to-year change is dominated by classification noise.
 * Shown near the year picker / yearly chart; we never headline a year-A-minus-B delta.
 */
export const GLC_FCS30D_CAVEAT =
  'Per-year snapshots; apparent year-to-year change includes classification noise and is not validated change detection.';

/**
 * Stronger caveat for the net-change panel. GLC_FCS30D classifies each epoch
 * independently, so a large fraction of apparent year-to-year difference is
 * classification noise (independent post-processing reduced global apparent change
 * by ~74%). The figures here are per-class snapshot differences derived from the
 * yearly areas — NOT measured land-cover transitions. There is no transition
 * matrix (that needs pixel-level co-occurrence, which only Earth Engine can compute
 * and which the contract deliberately does not expose). For validated forest-loss
 * change, use the Hansen / fire-loss layers.
 */
export const GLC_FCS30D_CHANGE_CAVEAT =
  'Indicative only — not validated change detection. GLC_FCS30D classifies each epoch independently, so much of the apparent year-to-year difference is classification noise (~74% of global apparent change is artefact). These are per-class snapshot differences, not measured transitions. For validated forest-loss change, use the Hansen / fire-loss layers.';
